// The eyes' own Spark SplatMesh, written from the GPU lift's packed ExtSplats words.

import { ExtSplats, SplatMesh } from '@sparkjsdev/spark'

export type EyeSplatMesh = {
  mesh: any
  count: number
  capacity: number
  updatePacked(words: Uint32Array): void
  dispose(): void
}

// Round up to a whole texture row: Spark FLOORS a partial row and drops the remainder.
const TEXTURE_ROW_WIDTH = 2048
const rowCapacity = (count: number) => Math.ceil(count / TEXTURE_ROW_WIDTH) * TEXTURE_ROW_WIDTH

export function createEyeSplatMesh(count: number): EyeSplatMesh {
  const capacity = rowCapacity(count)
  // extArrays (not maxSplats) is what makes Spark build its data textures up front.
  const extArrays: [Uint32Array, Uint32Array] = [
    new Uint32Array(capacity * 4),
    new Uint32Array(capacity * 4),
  ]
  const source = new ExtSplats({ extArrays, numSplats: count })
  const mesh = new SplatMesh({ splats: source })
  // Never frustum-culled: the eyes sit inside a head and Spark's bounds arrive late.
  mesh.frustumCulled = false

  // Two typed-array copies for the pair — the point of packing in the shader.
  function updatePacked(words: Uint32Array): void {
    const needed = capacity * 8
    if (words.length < needed) {
      throw new Error(`eye splats: packed words ${words.length} < ${needed} (capacity ${capacity})`)
    }
    extArrays[0].set(words.subarray(0, capacity * 4))
    extArrays[1].set(words.subarray(capacity * 4, needed))
    source.numSplats = count
    // A re-lift needs all three: array write, texture re-upload, version bump.
    for (const texture of source.textures) texture.needsUpdate = true
    mesh.needsUpdate = true
    mesh.updateVersion()
  }

  return {
    mesh,
    count,
    capacity,
    updatePacked,
    dispose() {
      mesh.parent?.remove(mesh)
      mesh.dispose?.()
      source.dispose?.()
    },
  }
}
